import { Modal } from '../../Modal/Modal';

import './styles.scss';

import { DEFAULT_LOCALE } from '@/constants/locales';
import { SupportedLocaleCode } from '@/services/locales/locales.models';
import { useCallback, useMemo, useState } from 'react';

import { ConfirmLandingChoices } from './components/ConfirmLandingChoices';
import { ConfirmLandingCreation } from './components/ConfirmLandingCreation';
import { LandingDisplayNameForm } from './components/LandingDisplayNameForm';
import { CREATION_MODE, LandingSourceStep } from './components/LandingSourceStep';
import { SupportedLocalesForm } from './components/SupportedLocalesForm';

import type { TenantListItem } from '@/actions/tenants';
import type { ReactNode } from 'react';
import type { CreationMode } from './components/LandingSourceStep';

export { CREATION_MODE } from './components/LandingSourceStep';
export type { CreationMode } from './components/LandingSourceStep';

export const CREATE_NEW_LANDING_MODAL_SLUG = 'create-new-landing-modal';

const STEPS = ['intro', 'source', 'name', 'locales', 'confirm'] as const;
type WizardStep = (typeof STEPS)[number];

const CreateNewLandingModal = () => {
  const [stepIndex, setStepIndex] = useState(0);
  const [creationMode, setCreationMode] = useState<CreationMode | null>(null);
  const [templateTenant, setTemplateTenant] = useState<TenantListItem | null>(null);
  const [landingName, setLandingName] = useState('');
  const [supportedLocales, setSupportedLocales] = useState<SupportedLocaleCode[]>([DEFAULT_LOCALE]);

  const step: WizardStep = STEPS[stepIndex];

  const resetNewLandingState = useCallback(() => {
    setStepIndex(0);
    setCreationMode(null);
    setTemplateTenant(null);
    setLandingName('');
    setSupportedLocales([DEFAULT_LOCALE]);
  }, []);

  const goNext = useCallback(() => {
    setStepIndex((i) => Math.min(i + 1, STEPS.length - 1));
  }, []);

  const goBack = useCallback(() => {
    setStepIndex((i) => Math.max(i - 1, 0));
  }, []);

  const handleSourceContinue = useCallback(() => {
    goNext();
  }, [goNext]);

  const handleModeSelect = useCallback((mode: CreationMode) => {
    setCreationMode(mode);
    // Only clear when leaving template mode — re-clicking "From template" must keep the selection.
    if (mode !== CREATION_MODE.TEMPLATE) {
      setTemplateTenant(null);
    }
  }, []);

  const stepContent: Record<WizardStep, ReactNode> = useMemo(
    () => ({
      intro: <ConfirmLandingCreation onContinue={goNext} />,
      source: (
        <LandingSourceStep
          creationMode={creationMode}
          selectedTenant={templateTenant}
          onModeSelect={handleModeSelect}
          onTemplateSelect={setTemplateTenant}
          onBack={goBack}
          onContinue={handleSourceContinue}
        />
      ),
      name: (
        <LandingDisplayNameForm
          landingName={landingName}
          onLandingNameChange={setLandingName}
          onBack={goBack}
          onContinue={(name) => {
            setLandingName(name);
            goNext();
          }}
        />
      ),
      locales: (
        <SupportedLocalesForm
          supportedLocales={supportedLocales}
          onSupportedLocalesChange={setSupportedLocales}
          onBack={goBack}
          onContinue={(locales) => {
            setSupportedLocales(locales);
            goNext();
          }}
        />
      ),
      confirm: (
        <ConfirmLandingChoices
          name={landingName}
          supportedLocales={supportedLocales}
          creationMode={creationMode ?? CREATION_MODE.EMPTY}
          templateTenant={templateTenant}
          onBack={goBack}
        />
      ),
    }),
    [creationMode, templateTenant, landingName, supportedLocales, goNext, goBack, handleModeSelect, handleSourceContinue],
  );

  return (
    <Modal
      slug={CREATE_NEW_LANDING_MODAL_SLUG}
      className="fv-create-new-landing-modal"
      onClose={resetNewLandingState}
      onExited={resetNewLandingState}
      aria-label="Create new landing"
    >
      <div className="fv-create-new-landing-modal__content" data-testid="create-new-landing-modal">
        {stepContent[step]}
      </div>
    </Modal>
  );
};

export default CreateNewLandingModal;
